import {gql} from '@apollo/client';
import {parseInteger} from '@benchling/js-utils';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {PlateSchema} from 'samples/storables/StorableSchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {InputChangeEvent} from 'util/Types';

import {PlateShapeTransform} from '../../RunSchemaSpecConstants';
import {PlateShapeFormQuery} from './__generated/PlateShapeFormQuery';
import {PlateShapeFormErrors, updateTransformationProperties} from './TransformFormUtils';

interface PlateShapeFormProps {
  transform: PlateShapeTransform;
  onChange: (transform: PlateShapeTransform) => void;
  formErrors: PlateShapeFormErrors;
}

export const PLATE_SHAPE_FORM_QUERY = gql`
  query PlateShapeFormQuery @skipAudit @engComponent(name: LAB_AUTOMATION) {
    ...AllPlateSchemasGraphQL
  }
  ${getFragmentWrapper('AllPlateSchemas').fragment}
`;

export default function PlateShapeForm(props: PlateShapeFormProps): JSX.Element {
  const schemasQueryResult = useBenchlingQuery<PlateShapeFormQuery>(PLATE_SHAPE_FORM_QUERY);
  let plateSchemas: Array<PlateSchema> = [];
  let selectedSchema: PlateSchema | null = null;
  if (schemasQueryResult.type === QueryResultType.SUCCESS) {
    plateSchemas = graphQLArrayToREST('PlateSchema', schemasQueryResult.data.plateSchemas);
    if (props.transform.transformationProperties?.plateSchema) {
      selectedSchema =
        plateSchemas.find((schema) => schema.id === props.transform.transformationProperties?.plateSchema) ??
        null;
    }
  } else {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <>
      <Row>
        <Col md={4}>
          <FieldLabel label="Plate schema" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <SelectDropdown
            alwaysDropDown={true}
            menu={
              <SearchableMenu
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.FULL}
                selectedValue={props.transform?.transformType}
                onSelect={(value: string) => {
                  props.onChange(updateTransformationProperties(props.transform, 'plateSchema', value));
                }}
                optionDatas={plateSchemas.map((option: PlateSchema) => ({
                  name: option.name,
                  value: option.id,
                }))}
              />
            }
            displayName={selectedSchema?.name || 'Select schema'}
            error={props.formErrors.plateSchema ? 'Plate schema is required' : null}
          />
        </Col>
        <Col md={4}>
          <TextField
            label="Index of A1 cell"
            placeholder="B3"
            onChange={(e) => {
              const value = e.target.value;
              const splitValue = value.match(/[a-zA-Z]+|[0-9]+/g);
              if (splitValue?.length !== 2) {
                const newTransformationProperties = {...props.transform.transformationProperties};
                delete newTransformationProperties.firstWellRowIndex;
                delete newTransformationProperties.firstWellColumnIndex;
                props.onChange({
                  ...props.transform,
                  transformationProperties: newTransformationProperties,
                });
                return;
              }
              const firstWellColumnIndex = ExcessUtil.colLettersToNumbers(splitValue[0]);
              const firstWellRowIndex = parseInteger(splitValue[1])! - 1;
              props.onChange({
                ...props.transform,
                transformationProperties: {
                  ...props.transform.transformationProperties,
                  firstWellRowIndex,
                  firstWellColumnIndex,
                },
              });
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={
              props.transform.transformationProperties?.firstWellRowIndex &&
              props.transform.transformationProperties?.firstWellColumnIndex
                ? ExcessUtil.toSpreadsheetIndex({
                    row: props.transform.transformationProperties.firstWellRowIndex,
                    column: props.transform.transformationProperties.firstWellColumnIndex,
                  })
                : undefined
            }
            hasError={props.formErrors.firstWellIndex}
          />
          {props.formErrors.firstWellIndex ? (
            <ErrorMessage error="Alphanumeric cell index is required" />
          ) : null}
        </Col>
      </Row>
      <CheckboxInput
        checked={props.transform.transformationProperties.deleteOtherCells}
        id="deleteOtherCellsCheckbox"
        label="Delete all other cells"
        onChange={(e: InputChangeEvent) => {
          props.onChange(
            updateTransformationProperties(props.transform, 'deleteOtherCells', e.target.checked)
          );
        }}
      />
    </>
  );
}
